/**
 * SettingsIntegrations — /website-builder/settings/integrations
 *
 * Lists 3rd-party services the user can flip on per site. Backed by:
 *  GET  /api/builder/integrations      → { items: [{id, name, category, enabled, config, config_fields}] }
 *  PUT  /api/builder/integrations/{id} → toggle + config save
 *
 * Secret fields are masked when stored — the UI sends back the mask string
 * if untouched, and backend keeps the existing secret.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plug, Loader2, ChevronDown, ChevronUp, BarChart3, Mail, Workflow, Headphones,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { GlassCard, GradientIconTile, DashboardBackdrop } from '@/components/dashboard/dashboard-ui';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CATEGORY_META = {
  analytics:  { label: 'Analytics & Tracking', icon: BarChart3,  ramp: 'violet'  },
  email:      { label: 'Email Marketing',      icon: Mail,       ramp: 'fuchsia' },
  automation: { label: 'Automation',           icon: Workflow,   ramp: 'cyan'    },
  support:    { label: 'Support & Chat',       icon: Headphones, ramp: 'amber'   },
};

function IntegrationCard({ item, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [config,   setConfig]   = useState(item.config || {});
  const [saving,   setSaving]   = useState(false);
  const cat = CATEGORY_META[item.category] || CATEGORY_META.analytics;

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/builder/integrations/${item.id}`, next, { headers: headers() });
      onChange(r.data);
      toast.success(`${item.name} updated`);
      setConfig(r.data.config || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => save({ enabled: !item.enabled, config });
  const saveConfig = () => save({ enabled: item.enabled, config });

  return (
    <GlassCard className={`p-5 transition-all ${item.enabled ? 'border-emerald-400/30' : ''}`} data-testid={`integration-card-${item.id}`}>
      <div className="flex items-start gap-4">
        <GradientIconTile icon={cat.icon} ramp={cat.ramp} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white font-bold font-syne text-sm">{item.name}</p>
            {item.enabled && (
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-400/25">
                On
              </span>
            )}
          </div>
          <p className="text-[11px] text-white/55 mt-0.5">{cat.label}</p>
        </div>
        <button
          onClick={toggle}
          disabled={saving}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${item.enabled ? 'bg-emerald-500' : 'bg-white/15'} disabled:opacity-50`}
          data-testid={`integration-toggle-${item.id}`}
          aria-pressed={item.enabled}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${item.enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      {/* Config fields */}
      {(item.config_fields?.length > 0) && (
        <>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-violet-300 hover:text-white transition-colors"
            data-testid={`integration-expand-${item.id}`}
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? 'Hide config' : 'Configure'}
          </button>
          {expanded && (
            <div className="mt-3 space-y-3 border-t border-white/[0.06] pt-4">
              {item.config_fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-[#A5A8C9] text-[11px]">{f.label}{f.secret && <span className="text-violet-300 ml-1">· encrypted</span>}</Label>
                  <Input
                    value={config[f.key] || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl"
                    data-testid={`integration-input-${item.id}-${f.key}`}
                  />
                </div>
              ))}
              <button
                onClick={saveConfig}
                disabled={saving}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_8px_22px_rgba(139,92,246,0.4)] transition-all disabled:opacity-50"
                data-testid={`integration-save-${item.id}`}
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : 'Save config'}
              </button>
            </div>
          )}
        </>
      )}
    </GlassCard>
  );
}

export default function SettingsIntegrations() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/integrations`, { headers: headers() });
      setItems(r.data?.items || []);
    } catch {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upsertItem = (updated) =>
    setItems((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));

  // Group by category
  const groups = items.reduce((acc, x) => {
    (acc[x.category] = acc[x.category] || []).push(x);
    return acc;
  }, {});

  return (
    <div className="relative" data-testid="settings-integrations">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-6 pb-12 max-w-4xl">
        <Link to="/website-builder/settings" data-testid="settings-go-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white hover:text-[#A78BFA] transition-colors font-semibold">
          <ArrowLeft size={12} /> Go Back
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Integrations</h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">Connect analytics, email tools, and automations to your store. Configure secrets safely — credentials are encrypted at rest.</p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-white/55"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : (
          Object.entries(groups).map(([cat, list]) => (
            <section key={cat}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-3">
                {CATEGORY_META[cat]?.label || cat}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {list.map((item) => (
                  <IntegrationCard key={item.id} item={item} onChange={upsertItem} />
                ))}
              </div>
            </section>
          ))
        )}

        {!loading && items.length === 0 && (
          <GlassCard className="p-8 text-center">
            <GradientIconTile icon={Plug} ramp="violet" size="lg" className="mx-auto" />
            <p className="text-white font-semibold font-syne mt-4">No integrations available</p>
            <p className="text-[12px] text-white/55 mt-1">Please contact support.</p>
          </GlassCard>
        )}
      </div>
    </div>
  );
}
